// ============================================
// ESTADO DA MENSALIDADE DA PLATAFORMA (régua de inadimplência)
//
// Store externo em vez de contexto porque duas fontes escrevem nele:
//   - o React, ao consultar GET /platform-plan/billing-state;
//   - o interceptor do axios, ao receber um 402 de qualquer requisição.
//
// O interceptor roda fora da árvore de componentes, então precisa de um ponto
// de escrita que não dependa de hooks.
// ============================================

import axios from 'axios';
import { getApiUrl } from '../config/environment';

export type BillingLadderState = 'ok' | 'warning' | 'overdue' | 'blocked';

export interface BillingInvoice {
  dueDate: string;
  valueCents: number;
  status: string;
  invoiceUrl: string | null;
  bankSlipUrl: string | null;
  identificationField: string | null;
  pixPayload: string | null;
  pixEncodedImage?: string | null;
}

/** Motivo do bloqueio: muda a tela que o cliente vê. */
export type BillingBlockReason = 'overdue' | 'activation_payment';

/** Contrato obrigatório assinado, aguardando o pagamento que libera o acesso. */
export interface BillingActivation {
  required: boolean;
  paid: boolean;
  contractId?: string | null;
  contractTitle?: string | null;
  valueCents?: number | null;
  invoice?: {
    asaasPaymentId: string;
    status: string;
    billingType: string | null;
    valueCents: number;
    dueDate: string;
    invoiceUrl: string | null;
    pixPayload: string | null;
    pixEncodedImage: string | null;
  } | null;
  chargeError?: string | null;
}

export interface PlatformBillingState {
  /** false quando o deploy não usa o hub de cobrança (provider legado). */
  configured: boolean;
  billingState: BillingLadderState;
  blocked: boolean;
  blockReason: BillingBlockReason | null;
  activation: BillingActivation | null;
  daysOverdue: number;
  graceDays: number | null;
  graceUntil: string | null;
  /** Só o Admin Master pode pagar; os demais veem orientação para contatá-lo. */
  canPay: boolean;
  /** Ativação é paga por quem assina o contrato: master ou administrador. */
  canPayActivation: boolean;
  invoice: BillingInvoice | null;
  loaded: boolean;
}

const INITIAL: PlatformBillingState = {
  configured: false,
  billingState: 'ok',
  blocked: false,
  blockReason: null,
  activation: null,
  daysOverdue: 0,
  graceDays: null,
  graceUntil: null,
  canPay: false,
  canPayActivation: false,
  invoice: null,
  loaded: false,
};

let snapshot: PlatformBillingState = INITIAL;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((fn) => fn());
}

export function subscribeBillingState(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getBillingSnapshot(): PlatformBillingState {
  return snapshot;
}

function setSnapshot(next: Partial<PlatformBillingState>) {
  snapshot = { ...snapshot, ...next };
  emit();
}

export function resetBillingState(): void {
  snapshot = INITIAL;
  emit();
}

/** Alimentado pelo interceptor quando alguma API responde 402. */
export function applyBlockedResponse(payload: {
  billingState?: BillingLadderState;
  blockReason?: BillingBlockReason;
  activation?: BillingActivation | null;
  daysOverdue?: number;
  graceDays?: number | null;
  graceUntil?: string | null;
  invoice?: BillingInvoice | null;
}): void {
  setSnapshot({
    configured: true,
    blocked: true,
    blockReason: payload.blockReason ?? 'overdue',
    activation: payload.activation ?? snapshot.activation,
    billingState: payload.billingState ?? 'blocked',
    daysOverdue: payload.daysOverdue ?? snapshot.daysOverdue,
    graceDays: payload.graceDays ?? snapshot.graceDays,
    graceUntil: payload.graceUntil ?? snapshot.graceUntil,
    invoice: payload.invoice ?? snapshot.invoice,
    loaded: true,
  });
}

let inFlight: Promise<void> | null = null;

/** Consulta o backend. Nunca lança: falha aqui não pode virar erro na tela. */
export function refreshBillingState(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = axios
    .get<{ success: boolean; data: Omit<PlatformBillingState, 'loaded'> }>(
      getApiUrl('/platform-plan/billing-state'),
    )
    .then((res) => {
      const data = res.data?.data;
      if (data) setSnapshot({ ...data, loaded: true });
    })
    .catch(() => {
      setSnapshot({ loaded: true });
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}
